/**
 * Dropdown Positioning Module
 * Implements viewport-aware dropdown positioning with automatic flip
 * when there is not enough space below the trigger element.
 *
 * Uses position: fixed so the dropdown breaks out of any ancestor
 * overflow containers (e.g. popups, scrollable panels).
 *
 * Automatically observes the DOM for elements with data-dropdown attribute
 * and applies positioning without requiring manual calls.
 */

/** Map to track cleanup functions for each dropdown element. */
const dropdownCleanupMap = new Map<HTMLElement, () => void>();

/** Map to store initial positioning data for each dropdown. */
const dropdownPositionDataMap = new Map<HTMLElement, {
    parentEl: HTMLElement;
    initialParentRect: DOMRect;
    computedWidth: number;
    direction: 'up' | 'down';
    left: number;
    scrollableParents: Set<HTMLElement>;
}>();

/** Breakpoint for mobile detection (matches CSS). */
const MOBILE_BREAKPOINT = 768;

/** Resize debounce timer. */
let resizeTimer: number | null = null;

/**
 * Select Dropdown Module for viewport-aware positioning.
 * Exported as part of window.Etask.Ui.SelectDropdownModule
 */
const DropdownModule = {
    observerInstance: null as MutationObserver | null,

    /**
     * Check if current viewport is mobile.
     */
    isMobile(): boolean {
        return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
    },

    /**
     * Berechnet den kumulativen Scroll-Offset aller scrollbaren Parent-Elemente.
     * @param element - Das Startelement
     * @returns Kumulierter Scroll-Offset in x und y
     */
    getCumulativeScrollOffset(element: HTMLElement): { x: number; y: number } {
        let scrollX = 0;
        let scrollY = 0;
        let currentElement: HTMLElement | null = element.parentElement;

        while (currentElement) {
            scrollX += currentElement.scrollLeft || 0;
            scrollY += currentElement.scrollTop || 0;
            currentElement = currentElement.parentElement;
        }

        return { x: scrollX, y: scrollY };
    },

    /**
     * Sammelt alle scrollbaren Parent-Elemente.
     * @param element - Das Startelement
     * @returns Set mit allen scrollbaren Parent-Elementen
     */
    getScrollableParents(element: HTMLElement): Set<HTMLElement> {
        const scrollableParents = new Set<HTMLElement>();
        let currentElement: HTMLElement | null = element.parentElement;

        while (currentElement) {
            const hasScrollX = currentElement.scrollWidth > currentElement.clientWidth;
            const hasScrollY = currentElement.scrollHeight > currentElement.clientHeight;

            if (hasScrollX || hasScrollY) {
                scrollableParents.add(currentElement);
            }

            currentElement = currentElement.parentElement;
        }

        return scrollableParents;
    },

    /**
     * Berechnet die clip-path basierend auf dem Viewport.
     * Schneidet das Dropdown ab, wenn es teilweise aus dem Viewport scrollt.
     * @param dropdownEl - Das Dropdown-Element
     * @returns Die clip-path CSS-Eigenschaft
     */
    calculateClipPath(dropdownEl: HTMLElement): string {
        const dropdownRect = dropdownEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Berechne wie viel vom Dropdown außerhalb des Viewports ist
        const top = Math.max(0, -dropdownRect.top);
        const left = Math.max(0, -dropdownRect.left);
        const right = Math.max(0, dropdownRect.right - viewportWidth);
        const bottom = Math.max(0, dropdownRect.bottom - viewportHeight);

        // Wenn nichts abgeschnitten werden muss, gib leeren String zurück
        if (top === 0 && left === 0 && right === 0 && bottom === 0) {
            return '';
        }

        return `inset(${top}px ${right}px ${bottom}px ${left}px)`;
    },

    /**
     * Aktualisiert die Position eines Dropdowns basierend auf Scroll-Änderungen.
     * @param dropdownEl - Das Dropdown-Element
     */
    updateDropdownPosition(dropdownEl: HTMLElement): void {
        const positionData = dropdownPositionDataMap.get(dropdownEl);
        if (!positionData) return;

        const { parentEl, computedWidth, direction } = positionData;
        
        // Aktuelle Position des Parent-Elements
        const currentParentRect = parentEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Prüfe, ob Parent-Element noch sichtbar ist
        const isParentVisible = 
            currentParentRect.bottom > 0 &&
            currentParentRect.top < viewportHeight &&
            currentParentRect.right > 0 &&
            currentParentRect.left < viewportWidth;

        if (!isParentVisible) {
            // Parent ist nicht mehr sichtbar - Dropdown verstecken (aber nicht schließen)
            dropdownEl.style.visibility = 'hidden';
            dropdownEl.style.clipPath = '';
            return;
        }

        // Parent ist wieder sichtbar - Dropdown anzeigen
        dropdownEl.style.visibility = 'visible';

        // Berechne neue Position basierend auf aktueller Parent-Position
        let left = currentParentRect.right - computedWidth;
        left = Math.max(0, Math.min(left, viewportWidth - computedWidth));
        
        dropdownEl.style.left = `${left}px`;

        // Aktualisiere vertikale Position basierend auf Richtung
        if (direction === 'down') {
            dropdownEl.style.top = `${currentParentRect.bottom}px`;
            dropdownEl.style.bottom = 'auto';
        } else {
            dropdownEl.style.top = 'auto';
            dropdownEl.style.bottom = `${viewportHeight - currentParentRect.top}px`;
        }

        // Berechne und wende Clipping an (nur in Y-Richtung relevant)
        const clipPath = this.calculateClipPath(dropdownEl);
        dropdownEl.style.clipPath = clipPath;
    },

    /**
     * Apply mobile fullscreen positioning for select dropdown.
     * On mobile devices, the dropdown is displayed fullscreen with CSS handling positioning.
     * 
     * @param dropdownEl - The dropdown element with data-dropdown attribute
     */
    applyMobilePosition(dropdownEl: HTMLElement): void {
        // On mobile, let CSS handle the fullscreen positioning
        // Remove any inline styles that might interfere
        dropdownEl.style.position = '';
        dropdownEl.style.width = '';
        dropdownEl.style.left = '';
        dropdownEl.style.right = '';
        dropdownEl.style.top = '';
        dropdownEl.style.bottom = '';
        dropdownEl.style.margin = '';
        dropdownEl.style.visibility = '';
        dropdownEl.removeAttribute('data-dropdown-direction');

        // Store original overflow values for restoration
        const originalOverflowValues = new Map<HTMLElement, string>();

        // Hide scrollbars on body
        originalOverflowValues.set(document.body, document.body.style.overflow);
        document.body.style.overflow = 'hidden';

        // Find and hide scrollbars on all scrollable elements outside the dropdown
        const allElements = document.querySelectorAll<HTMLElement>('*');
        allElements.forEach(element => {
            // Skip if element is the dropdown itself or inside it
            if (element === dropdownEl || dropdownEl.contains(element)) {
                return;
            }

            // Check if element has scrollable overflow
            const computedStyle = window.getComputedStyle(element);
            const overflowX = computedStyle.overflowX;
            const overflowY = computedStyle.overflowY;
            
            if (overflowX === 'auto' || overflowX === 'scroll' || 
                overflowY === 'auto' || overflowY === 'scroll') {
                // Store original overflow value
                originalOverflowValues.set(element, element.style.overflow);
                // Hide scrollbar
                element.style.overflow = 'hidden';
            }
        });

        // Store cleanup function
        const cleanup = () => {
            // Restore all overflow values
            originalOverflowValues.forEach((originalValue, element) => {
                element.style.overflow = originalValue;
            });
            dropdownCleanupMap.delete(dropdownEl);
        };
        dropdownCleanupMap.set(dropdownEl, cleanup);
    },

    /**
     * Apply desktop fixed positioning for select dropdown.
     * Positions the dropdown relative to its parent element with viewport-aware
     * flip behavior (up/down) when space is limited.
     *
     * @param dropdownEl - The dropdown element with data-dropdown attribute
     */
    applyDesktopPosition(dropdownEl: HTMLElement): void {
        const parentEl = dropdownEl.parentElement;
        if (!parentEl) return;

        // Measure rendered width while still position: absolute
        const computedWidth = dropdownEl.offsetWidth;
        const dropdownHeight = dropdownEl.scrollHeight || dropdownEl.offsetHeight;

        const parentRect = parentEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const spaceBelow = viewportHeight - parentRect.bottom;
        const spaceAbove = parentRect.top;

        // Right-align dropdown with the parent (matches CSS right: 0 behavior).
        // Clamp to viewport so the dropdown never goes off-screen.
        let left = parentRect.right - computedWidth;
        left = Math.max(0, Math.min(left, viewportWidth - computedWidth));
        
        // Switch to fixed positioning to escape overflow containers.
        dropdownEl.style.position = 'fixed';
        dropdownEl.style.width = `${computedWidth}px`;
        dropdownEl.style.left = `${left}px`;
        dropdownEl.style.right = 'auto';
        dropdownEl.style.margin = '0';

        // Prefer opening downward; flip only when there is not enough
        // space below AND more space is available above.
        const direction = (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) ? 'down' : 'up';
        
        if (direction === 'down') {
            dropdownEl.style.top = `${parentRect.bottom}px`;
            dropdownEl.style.bottom = 'auto';
            dropdownEl.setAttribute('data-dropdown-direction', 'down');
        } else {
            dropdownEl.style.top = 'auto';
            dropdownEl.style.bottom = `${viewportHeight - parentRect.top}px`;
            dropdownEl.setAttribute('data-dropdown-direction', 'up');
        }

        // Reveal the dropdown now that positioning is fully applied.
        dropdownEl.style.visibility = 'visible';

        // Sammle alle scrollbaren Parent-Elemente
        const scrollableParents = this.getScrollableParents(dropdownEl);

        // Speichere Positionierungsdaten für spätere Updates
        dropdownPositionDataMap.set(dropdownEl, {
            parentEl,
            initialParentRect: parentRect,
            computedWidth,
            direction,
            left,
            scrollableParents
        });

        const handleScroll = (e: Event) => {
            // Ignore scrolls originating inside the dropdown itself
            if (e.target instanceof Node && dropdownEl.contains(e.target)) return;
            
            // Aktualisiere die Position des Dropdowns beim Scrollen
            this.updateDropdownPosition(dropdownEl);
        };

        // Register scroll listeners auf allen scrollbaren Parents
        scrollableParents.forEach(parent => {
            parent.addEventListener('scroll', handleScroll, { passive: true });
        });

        // Auch globale scroll events abfangen (für body/window scroll)
        window.addEventListener('scroll', handleScroll, { capture: true, passive: true });

        // Store cleanup function
        const cleanup = () => {
            scrollableParents.forEach(parent => {
                parent.removeEventListener('scroll', handleScroll);
            });
            window.removeEventListener('scroll', handleScroll, { capture: true });
            dropdownPositionDataMap.delete(dropdownEl);
            dropdownCleanupMap.delete(dropdownEl);
        };
        dropdownCleanupMap.set(dropdownEl, cleanup);
    },

    /**
     * Position a single dropdown element based on viewport size.
     * @param dropdownEl - The dropdown element with data-dropdown attribute
     */
    positionDropdown(dropdownEl: HTMLElement): void {
        if (!dropdownEl) return;

        // Clean up any previous listeners for this dropdown
        this.cleanupDropdown(dropdownEl);

        // Apply positioning based on viewport
        if (this.isMobile()) {
            this.applyMobilePosition(dropdownEl);
        } else {
            this.applyDesktopPosition(dropdownEl);
        }
    },

    /**
     * Clean up a specific dropdown element.
     * @param dropdownEl - The dropdown element to clean up
     */
    cleanupDropdown(dropdownEl: HTMLElement): void {
        // Restore body scroll
        document.body.style.overflow = '';

        const cleanup = dropdownCleanupMap.get(dropdownEl);
        if (cleanup) {
            cleanup();
        }
    },

    /**
     * Position all dropdown elements currently in the DOM.
     */
    positionAllDropdowns(): void {
        const dropdowns = document.querySelectorAll<HTMLElement>('[data-dropdown]');
        dropdowns.forEach(dropdown => this.positionDropdown(dropdown));
    },

    /**
     * Observe the DOM for dropdown elements and position them automatically.
     */
    observeDropdowns(): void {
        // Position any existing dropdowns
        this.positionAllDropdowns();

        // Setup global resize handler with debouncing
        window.addEventListener('resize', this.handleGlobalResize.bind(this));

        // Stop any existing observer
        if (this.observerInstance) {
            this.observerInstance.disconnect();
        }

        // Create new MutationObserver
        this.observerInstance = new MutationObserver((mutations) => {
            mutations.forEach(mutation => {
                // Check for added nodes with data-dropdown attribute
                mutation.addedNodes.forEach(node => {
                    if (node instanceof HTMLElement) {
                        if (node.hasAttribute('data-dropdown')) {
                            this.positionDropdown(node);
                        }
                        // Also check descendants
                        const dropdowns = node.querySelectorAll<HTMLElement>('[data-dropdown]');
                        dropdowns.forEach(dropdown => this.positionDropdown(dropdown));
                    }
                });

                // Check for removed nodes and clean up
                mutation.removedNodes.forEach(node => {
                    if (node instanceof HTMLElement) {
                        if (node.hasAttribute('data-dropdown')) {
                            this.cleanupDropdown(node);
                        }
                        // Also check descendants
                        const dropdowns = node.querySelectorAll<HTMLElement>('[data-dropdown]');
                        dropdowns.forEach(dropdown => this.cleanupDropdown(dropdown));
                    }
                });

                // Check for attribute changes
                if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
                    const target = mutation.target;
                    if (mutation.attributeName === 'data-dropdown') {
                        if (target.hasAttribute('data-dropdown')) {
                            this.positionDropdown(target);
                        } else {
                            this.cleanupDropdown(target);
                        }
                    }
                }
            });
        });

        // Start observing the entire document
        this.observerInstance.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-dropdown']
        });
    },

    /**
     * Stop observing the DOM and clean up all dropdowns.
     */
    stopObserving(): void {
        if (this.observerInstance) {
            this.observerInstance.disconnect();
            this.observerInstance = null;
        }

        // Remove global resize handler
        window.removeEventListener('resize', this.handleGlobalResize);

        // Clear debounce timer
        if (resizeTimer !== null) {
            window.clearTimeout(resizeTimer);
            resizeTimer = null;
        }

        // Clean up all tracked dropdowns
        dropdownCleanupMap.forEach((cleanup) => {
            cleanup();
        });
        dropdownCleanupMap.clear();

        // Restore body scroll
        document.body.style.overflow = '';
    },

    /**
     * Global resize handler (needed for proper cleanup).
     */
    handleGlobalResize(): void {
        // Clear previous timer
        if (resizeTimer !== null) {
            window.clearTimeout(resizeTimer);
        }
        
        // Debounce resize event (wait 100ms after last resize event)
        resizeTimer = window.setTimeout(() => {
            DropdownModule.positionAllDropdowns();
            resizeTimer = null;
        }, 100);
    },

    /**
     * Dispose of all resources.
     * @deprecated Use stopObserving() instead for consistency with other modules.
     */
    dispose(): void {
        this.stopObserving();
    }
};

// Automatically start observing when module loads
if (typeof document !== 'undefined') {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            DropdownModule.observeDropdowns();
        });
    } else {
        // DOM is already ready
        DropdownModule.observeDropdowns();
    }
}

export default DropdownModule;
